import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { RootState, useAppSelector } from 'src/store';
import { useBlocklistApi } from '../api';
import { resetError, resetList, setError, setFilter, setList, setLoading } from '../slices';

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    dateCreated: 'date_created',
    dateUpdated: 'date_updated',
  };

  const [field, order] = sort.split('_');
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useBlocklistsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector(state => state.blocklist);

  const api = useBlocklistApi();

  const getBlocklist = useCallback(async (companyId?: string) => {
    const filter = getState().blocklist.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getBlocklist({
        companyId,
        limit: filter.limit,
        offset: filter.offset,
        query: filter.query || undefined,
        sort: getServerSortField(filter.sort),
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeBlocklistFilter = useCallback((filter: Partial<RootState['blocklist']['filter']>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetBlocklist = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getBlocklist,
    changeBlocklistFilter,
    resetBlocklist,
  };
};